import { CalibrationPoint, Ramp } from "../../../../types";

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

function toDegrees(rad: number): number {
  return (rad * 180) / Math.PI;
}

function latlonToMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): [number, number] {
  const R = 6371000; // Earth radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const avgLat = toRadians((lat1 + lat2) / 2);

  const dx = dLon * R * Math.cos(avgLat);
  const dy = dLat * R;
  return [dx, dy];
}

export function estimateCanvasOrigin(
  points: CalibrationPoint[]
): [number, number] {
  // Need at least 2 points to estimate
  if (points.length < 2) {
    throw new Error("Need at least 2 calibration points to estimate origin");
  }

  // Use the first point as a reference
  const reference = points[0];

  // Calculate scaling factors using all other points relative to the reference
  let totalSx = 0;
  let totalSy = 0;
  let validPoints = 0;

  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    const dxCanvas = point.x - reference.x;
    const dyCanvas = point.y - reference.y;

    // Skip points that are too close in canvas space
    if (Math.abs(dxCanvas) < 1e-10 || Math.abs(dyCanvas) < 1e-10) {
      continue;
    }

    const [dxGeo, dyGeo] = latlonToMeters(
      reference.latitude,
      reference.longitude,
      point.latitude,
      point.longitude
    );

    totalSx += dxGeo / dxCanvas;
    totalSy += dyGeo / dyCanvas;
    validPoints++;
  }

  if (validPoints === 0) {
    throw new Error("Could not calculate valid scaling factors");
  }

  // Average scaling factors
  const sx = totalSx / validPoints;
  const sy = totalSy / validPoints;

  // Calculate origin using the reference point and average scaling
  const dxM = -reference.x * sx;
  const dyM = -reference.y * sy;

  const lat0 = reference.latitude;
  const lon0 = reference.longitude;

  const originLat = lat0 + toDegrees(dyM / 6371000);
  const originLon =
    lon0 + toDegrees(dxM / (6371000 * Math.cos(toRadians(lat0))));

  return [originLat, originLon];
}

export function calculateRampBoundingBox(ramp: Ramp): {
  topLeft: [number, number];
  topRight: [number, number];
  bottomLeft: [number, number];
  bottomRight: [number, number];
} | null {
  // Need at least 2 calibration points and valid dimensions
  if (
    !ramp.calibration_points ||
    ramp.calibration_points.length < 2 ||
    !ramp.width ||
    !ramp.depth
  ) {
    return null;
  }

  try {
    // Get the origin (top-left corner)
    const [originLat, originLon] = estimateCanvasOrigin(
      ramp.calibration_points
    );

    // Calculate scaling factors from calibration points
    const reference = ramp.calibration_points[0];
    let validPoints = 0;

    for (let i = 1; i < ramp.calibration_points.length; i++) {
      const point = ramp.calibration_points[i];
      const dxCanvas = point.x - reference.x;
      const dyCanvas = point.y - reference.y;

      if (Math.abs(dxCanvas) < 1e-10 || Math.abs(dyCanvas) < 1e-10) {
        continue;
      }
      validPoints++;
    }

    if (validPoints === 0) {
      return null;
    }

    // Convert feet to meters if needed
    const widthMeters = ramp.width * 0.3048; // feet to meters
    const depthMeters = ramp.depth * 0.3048; // feet to meters

    // Calculate the four corners
    // Top-left is the origin
    const topLeft: [number, number] = [originLat, originLon];

    // Top-right: move east by width
    const topRightLon =
      originLon +
      toDegrees(widthMeters / (6371000 * Math.cos(toRadians(originLat))));
    const topRight: [number, number] = [originLat, topRightLon];

    // Bottom-left: move south by depth
    const bottomLeftLat = originLat - toDegrees(depthMeters / 6371000);
    const bottomLeft: [number, number] = [bottomLeftLat, originLon];

    // Bottom-right: move both south and east
    const bottomRight: [number, number] = [bottomLeftLat, topRightLon];

    return {
      topLeft,
      topRight,
      bottomLeft,
      bottomRight,
    };
  } catch (error) {
    console.warn("Failed to calculate bounding box:", error);
    return null;
  }
}
